/**
 * Attention Queue — the list-first "what needs me, everywhere" home.
 *
 * One cross-context ranked stream of the open action cards a person should look
 * at next, NOT a node canvas and NOT scoped to a single context like the
 * per-context Recommendation Stream (`GET /contexts/:id/action-cards`). It
 * answers "across all my governed contexts, what is parked on me, ranked?".
 *
 * Lens/projection doctrine (technical/06, `cards-propose-runtime-executes`):
 * this is a READ projection of authorized data. It never mutates business truth.
 * Each card deep-links to where the server-enforced lifecycle actually lives —
 * the Action Card detail page (`/action-cards/:id`, which owns
 * accept/defer/reject/reopen) and the originating context. The queue itself only
 * ranks and routes; the worker enforces every state change.
 *
 * Data source: `GET /action-cards` — the global, paginated action-card list that
 * already exists (Wave 4 action-cards runtime). The queue requests the open,
 * actionable statuses, ranks them by `priority_score desc` then `last_seen_at
 * desc`, and surfaces an overflow banner when more cards exist than the page
 * shows. There is no dedicated cross-context "attention stream" packet endpoint
 * yet; when the list endpoint is absent (404/405) the screen degrades to a
 * truthful "proposed" empty state, exactly as the design system mandates for a
 * screen whose backing read is not built.
 *
 * Eight-state contract: rendered through the shared `RenderState` (S13) so
 * loading / empty / normal / overflow / error read identically to every other
 * V3 surface. `redacted` is surfaced inline via the per-card redacted-support
 * count; `forbidden` and `stale` are reachable through the same component if the
 * backing read later reports them.
 */
import * as React from "react";
import { useGo } from "@refinedev/core";

import {
  ACTION_CARD_SEVERITIES,
  type ActionCard,
  type ActionCardSeverity,
  type ActionCardStatus,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { InspectorSection } from "@/components/inspector";
import { ApiError, api } from "@/lib/api";
import { formatRelative, humanize } from "@/lib/format";

/** Open, actionable card statuses — the ones that still want a human. */
const OPEN_STATUSES: readonly ActionCardStatus[] = [
  "proposed",
  "deferred",
  "accepted",
  "failed",
];

/** Severity rank so the stream sorts critical-first within equal scores. */
const SEVERITY_RANK: Record<ActionCardSeverity, number> = {
  critical: 3,
  high: 2,
  medium: 1,
  low: 0,
};

/** Page size requested from the global list (overflow is disclosed past this). */
const PAGE_SIZE = 50;

/** Shape of the global action-card list projection (`GET /action-cards`). */
interface ActionCardListResponse {
  items: ActionCard[];
  total: number;
}

interface AttentionQueueData {
  cards: ActionCard[];
  total: number;
}

function useAttentionQueue() {
  const [data, setData] = React.useState<AttentionQueueData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  /** Distinguishes "endpoint not built" from "built but errored" (§proposed). */
  const [proposed, setProposed] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((v) => v + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProposed(false);
    api
      .get<ActionCardListResponse>("/action-cards", {
        query: { pageSize: PAGE_SIZE, page: 1 },
      })
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        setData({ cards: items, total: typeof res?.total === "number" ? res.total : items.length });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 404/405 means the cross-context attention read is not built yet.
        if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
          setData({ cards: [], total: 0 });
          setProposed(true);
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load the attention queue.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return { data, loading, error, proposed, reload };
}

/** Icon for a card's suggested next action — quiet, semantic, HugeIcons-only. */
function suggestedActionIcon(card: ActionCard): IconName {
  switch (card.suggested_action_type) {
    case "open_decision":
    case "review_transition":
      return "decisions";
    case "request_role":
    case "request_delegation":
    case "request_credential_review":
      return "roles";
    case "request_evidence":
      return "evidence";
    case "dispatch_verifier":
    case "retry_agent_run":
      return "agentRuns";
    case "promote_context":
    case "update_monitoring_profile":
      return "contexts";
    case "dismiss":
    case "no_op_review":
      return "defer";
    default:
      return "actionCards";
  }
}

/** Severity → badge variant, so critical reads hot and low reads quiet. */
function severityVariant(
  severity: ActionCardSeverity,
): "destructive" | "warning" | "secondary" | "outline" {
  if (severity === "critical") return "destructive";
  if (severity === "high") return "warning";
  if (severity === "medium") return "secondary";
  return "outline";
}

function formatScore(value: number | null): string {
  if (value === null || value === undefined) return "—";
  return value.toFixed(2);
}

function StreamCard({ card }: { card: ActionCard }) {
  const go = useGo();
  const human = card.approval_mode === "human_only";
  return (
    <Card data-testid="attention-card">
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">{card.title}</CardTitle>
            {card.summary ? (
              <p className="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {card.summary}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <StatusBadge status={card.status} />
            <span className="font-mono text-xs tabular-nums text-muted-foreground">
              score {formatScore(card.priority_score)}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-4 pt-0">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant={severityVariant(card.severity)}>{humanize(card.severity)}</Badge>
          <Badge variant="outline">
            <Icon name="scope" aria-hidden />
            {humanize(card.risk_level)} risk
          </Badge>
          <Badge variant="outline">{humanize(card.category)}</Badge>
          {human ? (
            <Badge variant="warning">
              <Icon name="user" aria-hidden />
              Human only
            </Badge>
          ) : (
            <Badge variant="outline">{humanize(card.approval_mode)}</Badge>
          )}
          {card.occurrence_count > 1 ? (
            <Badge variant="secondary">×{card.occurrence_count} seen</Badge>
          ) : null}
        </div>

        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          <Icon name={suggestedActionIcon(card)} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <span className="font-medium">{humanize(card.suggested_action_type)}</span>
            <p className="text-muted-foreground [overflow-wrap:anywhere]">{card.reason}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Icon name="contexts" aria-hidden />
            <span className="font-mono">{card.context_id}</span>
            {" · "}
            last seen {formatRelative(card.last_seen_at)}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => go({ to: `/action-cards/${card.id}` })}
              data-testid="attention-card-open"
            >
              <Icon name="deepLink" data-icon="inline-start" aria-hidden />
              Open card
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => go({ to: `/contexts/${card.context_id}` })}
            >
              <Icon name="contexts" data-icon="inline-start" aria-hidden />
              Context
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AttentionQueue() {
  const go = useGo();
  const { data, loading, error, proposed, reload } = useAttentionQueue();
  const [severityFilter, setSeverityFilter] = React.useState<string>("");
  const [statusFilter, setStatusFilter] = React.useState<string>("");

  const allCards = data?.cards ?? [];

  // Open, ranked, cross-context stream. Ranking: priority_score desc, then
  // severity, then most-recently-seen — a stable "what wants me next" order.
  const ranked = React.useMemo(() => {
    return allCards
      .filter((card) => OPEN_STATUSES.includes(card.status))
      .filter((card) => (severityFilter ? card.severity === severityFilter : true))
      .filter((card) => (statusFilter ? card.status === statusFilter : true))
      .slice()
      .sort((a, b) => {
        const score = (b.priority_score ?? 0) - (a.priority_score ?? 0);
        if (score !== 0) return score;
        const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
        if (sev !== 0) return sev;
        return b.last_seen_at.localeCompare(a.last_seen_at);
      });
  }, [allCards, severityFilter, statusFilter]);

  // Summary across the open, unfiltered set (the queue's true backlog).
  const open = React.useMemo(
    () => allCards.filter((card) => OPEN_STATUSES.includes(card.status)),
    [allCards],
  );
  const summary = React.useMemo(
    () => ({
      open: open.length,
      proposed: open.filter((c) => c.status === "proposed").length,
      critical: open.filter((c) => c.severity === "critical").length,
      humanOnly: open.filter((c) => c.approval_mode === "human_only").length,
    }),
    [open],
  );

  // Overflow when the global backlog exceeds what this page fetched/shows.
  const overflowCount = Math.max(0, (data?.total ?? 0) - allCards.length);

  const state: RenderStateKind = loading
    ? "loading"
    : error
      ? "error"
      : ranked.length === 0
        ? "empty"
        : overflowCount > 0
          ? "overflow"
          : "normal";

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Attention Queue"
          subtitle="Everything that needs you across every context, ordered by priority into one stream. A read projection — open a card to act; the runtime enforces every decision."
          icon="actionCards"
          actions={
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => go({ to: "/approval-center" })}>
                <Icon name="approve" data-icon="inline-start" aria-hidden />
                Approvals
              </Button>
              <Button variant="outline" size="sm" onClick={reload}>
                <Icon name="reopen" data-icon="inline-start" aria-hidden />
                Refresh
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <SummaryTile label="Open" value={summary.open} icon="actionCards" />
          <SummaryTile label="Proposed" value={summary.proposed} icon="events" />
          <SummaryTile label="Critical" value={summary.critical} icon="reject" />
          <SummaryTile label="Human only" value={summary.humanOnly} icon="user" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={severityFilter || "__all__"}
            onValueChange={(v) => setSeverityFilter(v === "__all__" ? "" : v)}
          >
            <SelectTrigger className="w-[180px]" data-testid="attention-severity-filter">
              <SelectValue placeholder="All severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All severities</SelectItem>
              {ACTION_CARD_SEVERITIES.map((severity) => (
                <SelectItem key={severity} value={severity}>
                  {humanize(severity)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter || "__all__"}
            onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}
          >
            <SelectTrigger className="w-[180px]" data-testid="attention-status-filter">
              <SelectValue placeholder="All open statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All open statuses</SelectItem>
              {OPEN_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {humanize(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <InspectorSection title="Ranked stream">
          <RenderState
            state={state}
            loadingRows={5}
            error={error}
            errorMessage={error ?? undefined}
            onRetry={reload}
            overflowCount={overflowCount}
            overflowMessage={`${overflowCount} more open card${overflowCount === 1 ? "" : "s"} beyond this page — narrow with filters or open a context to see the rest.`}
            empty={{
              icon: "actionCards",
              title: proposed
                ? "Cross-context attention stream not built yet"
                : "Nothing needs you right now",
              hint: proposed
                ? "Action cards exist per context; the global attention read (GET /action-cards) ships in a follow-up slice. Open a context's Recommendation Stream in the meantime."
                : "Open action cards from any governed context will surface here, ranked by priority — newest and most urgent first.",
            }}
          >
            <div className="flex flex-col gap-3">
              {ranked.map((card) => (
                <StreamCard key={card.id} card={card} />
              ))}
            </div>
          </RenderState>
        </InspectorSection>
      </div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: IconName;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
        <Icon name={icon} className="h-4 w-4 text-muted-foreground" aria-hidden />
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <div className="text-2xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

export default AttentionQueue;
